import { FC, useEffect, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import ExpandIcon from "@mui/icons-material/ChevronRight";
import CollapseIcon from "@mui/icons-material/UnfoldLess";
import { Button, Grid, Stack } from "@mui/material";
import { RichTreeView } from "@mui/x-tree-view";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import ConfirmDelete from "../../../../components/common/ConfirmDelete";
import FullScreenDialog from "../../../../components/common/FullScreenDialog";
import FormReadOnlyField from "../../../../components/forms/FormReadOnlyField";
import FormSectionTitle from "../../../../components/forms/FormSectionTitle";
import FormSwitchField from "../../../../components/forms/FormSwitchField";
import FormTextField from "../../../../components/forms/FormTextField";
import { RoleDTO, RolePayload, RolePermissionDTO } from "../../../../datasources/api-client/data-contracts";
import { ApiRoutes } from "../../../../datasources/networking/apiRoutes";
import { emptyRolePayload, useRoleValidationSchemas } from "../../../../datasources/requests/adminRoles";
import { NonPaginatedResponse } from "../../../../datasources/responses/common";
import { useResource } from "../../../../hooks/useResource";
import { useResources } from "../../../../hooks/useResources";
import { usePermissionTree } from "./usePermissionsTree";

interface Props {
	id?: string;
	isOpen: boolean;
	onCancel: () => void;
	onSuccess: () => void;
}

/**
 * RoleDetailsDialog renders the full-screen dialog that allows users to edit a role.
 *
 * @param id
 * @param isOpen
 * @param onCancel
 * @param onSuccess
 * @constructor
 */
const RoleDetailsDialog: FC<Props> = ({ id, isOpen, onCancel, onSuccess }) => {
	const { t } = useTranslation("feature.roles");
	const { UpsertRoleSchema } = useRoleValidationSchemas();

	const [askConfirmDelete, setAskConfirmDelete] = useState(false);
	const [selectedPermissions, setSelectedPermissions] = useState<string[]>([]);

	const {
		data: role,
		isLoading: roleIsLoading,
		reload: reloadRole,
		post,
		put,
		del,
	} = useResource<RolePayload, RoleDTO>({ route: ApiRoutes.AdminRoles, resourceId: id });

	const {
		data: permissions,
		isLoading: permissionsAreLoading,
		reload: reloadPermissions,
	} = useResources<NonPaginatedResponse<RolePermissionDTO>>({ route: ApiRoutes.AdminPermissions });

	const reload = async () => {
		await reloadPermissions();

		if (id) {
			reloadRole();
		}
	};

	const { getAllDescendants, getAllAncestors, childMap, parentMap } = usePermissionTree(permissions?.items);

	const form = useForm<RolePayload>({
		defaultValues: { ...emptyRolePayload },
		resolver: zodResolver(UpsertRoleSchema),
	});

	useEffect(() => {
		if (!permissions?.items?.length || !role) return;

		// Reset form with role data
		form.reset(
			{
				description: role?.description,
				enabled: id && id !== "" ? role?.enabled : true,
				permissions: role?.selectedPermissions ?? [],
			},
			{ keepErrors: true }
		);

		// Compute correct initially selected permissions.
		const selected = new Set(role.selectedPermissions ?? []) as Set<string>;

		// Function to compute parent selections based on current selections.
		const computeParentSelections = () => {
			let changed = false;

			// Iterate through each selected permission.
			for (const id of Array.from(selected)) {
				let currentId = id as string;

				// Traverse up the hierarchy to find parents.
				while (parentMap.has(currentId)) {
					const parentId = parentMap.get(currentId)!;
					const siblings = childMap.get(parentId) ?? [];

					// If all siblings are selected, select the parent.
					// This ensures that if all children of a parent have been selected,
					// the parent itself will be marked as selected.
					if (siblings.every((siblingId) => selected.has(siblingId))) {
						if (!selected.has(parentId)) {
							selected.add(parentId);
							changed = true;
						}
					}
					currentId = parentId;
				}
			}

			// Now check if any parents are deselected.
			if (changed) computeParentSelections();
		};

		// Start with the current selections and compute parents.
		computeParentSelections();
		// Set the selected permissions state
		setSelectedPermissions(Array.from(selected));
	}, [id, role, permissions?.items, form, parentMap, childMap]);

	// Handle selection changes in the RichTreeView.
	// It ensures that when a permission is selected or deselected,
	// its descendants and ancestors are also updated accordingly.
	const handleSelectionChange = (_: any, newSelected: string[]) => {
		// Find the last toggled permission.
		// This is the permission that was either selected or deselected last.
		const lastToggled =
			newSelected.find((id) => !selectedPermissions.includes(id)) ??
			selectedPermissions.find((id) => !newSelected.includes(id));

		// If no permission was toggled, exit early.
		if (!lastToggled) return;

		const isSelected = selectedPermissions.includes(lastToggled);
		const updated = new Set(selectedPermissions);

		// If the last toggled permission is not selected, we add it and its descendants.
		if (!isSelected) {
			// Add the last toggled permission and all its descendants.
			updated.add(lastToggled);

			// Add all ancestors if all their children are selected.
			getAllDescendants(lastToggled).forEach((id) => updated.add(id));

			// Check if all children of each ancestor are selected.
			// If so, add the ancestor to the updated set.
			getAllAncestors(lastToggled).forEach((parentId) => {
				const allChildren = getAllDescendants(parentId);
				if (allChildren.every((childId) => updated.has(childId))) {
					updated.add(parentId);
				}
			});
		} else {
			// If the last toggled permission is selected, we remove it and its descendants.
			updated.delete(lastToggled);
			getAllDescendants(lastToggled).forEach((id) => updated.delete(id));
			getAllAncestors(lastToggled).forEach((parentId) => updated.delete(parentId));
		}

		// Update the selected permissions state.
		setSelectedPermissions(Array.from(updated));
	};

	const handleClickSend = () => {
		const values = form.getValues();
		values.permissions = selectedPermissions.filter((p) => !p.startsWith("_"));

		if (id) {
			put(values, { onSuccess });
		} else {
			post(values, { onSuccess });
		}
	};

	const closeConfirmDelete = () => setAskConfirmDelete(false);
	const confirmDelete = () => {
		closeConfirmDelete();
		del({ onSuccess });
	};

	const isLoading = roleIsLoading || permissionsAreLoading;
	const description = form.watch("description");
	const title = id ? t("edit role", { context: role?.isProtected ? "locked" : "unlocked" }) : t("create role");

	return (
		<>
			<FullScreenDialog isOpen={isOpen} onClose={onCancel} onReload={reload} title={title} isLoading={isLoading}>
				<Grid container spacing={2} sx={{ overflowY: "auto" }}>
					<Grid size={12}>
						<FormSectionTitle title={t("registry")} />
					</Grid>
					<Grid size={{ xs: 12, sm: 6 }}>
						{role?.isProtected && (
							<FormReadOnlyField
								isLoading={isLoading}
								fullWidth
								label={t("description")}
								value={role?.description ?? ""}
							/>
						)}

						{!role?.isProtected && (
							<FormTextField
								fullWidth
								disabled={isLoading}
								isLoading={isLoading}
								label={t("description")}
								form={form}
								variant="outlined"
								name="description"
							/>
						)}
					</Grid>
					<Grid size={{ xs: 12, sm: 6 }}>
						<FormSwitchField
							disabled={role?.isProtected}
							name="enabled"
							isLoading={isLoading}
							form={form}
							label={t("enabled")}
						/>
					</Grid>

					{!isLoading && (
						<>
							<Grid size={12}>
								<FormSectionTitle title={t("permissions")} />
							</Grid>
							<Grid size={12}>
								<RichTreeView
									items={permissions?.items ?? []}
									checkboxSelection
									multiSelect
									selectionPropagation={{ parents: false, descendants: true }}
									selectedItems={selectedPermissions}
									slots={{ expandIcon: ExpandIcon, collapseIcon: CollapseIcon }}
									disableSelection={role?.isProtected ?? false}
									onSelectedItemsChange={handleSelectionChange}
								/>
							</Grid>
						</>
					)}
					{!role?.isProtected && (
						<Grid size={12} textAlign="end">
							<Stack direction="row-reverse" spacing={2}>
								{role?.id && (
									<Button
										className="cancel"
										loading={isLoading}
										loadingPosition="start"
										onClick={() => setAskConfirmDelete(true)}
									>
										{t("delete", { ns: "common" })}
									</Button>
								)}
								<Button
									className="confirm"
									disabled={!form.formState.isValid}
									loading={isLoading}
									loadingPosition="start"
									onClick={handleClickSend}
								>
									{t("apply", { ns: "common" })}
								</Button>
							</Stack>
						</Grid>
					)}
				</Grid>
			</FullScreenDialog>

			<ConfirmDelete
				confirmCode={description}
				isOpen={askConfirmDelete}
				onConfirm={confirmDelete}
				confirmLabel={t("description")}
				onCancel={closeConfirmDelete}
				title={t("do you really want to delete this role?")}
				confirmHint={t("type description {description}, then confirm to delete user", { description })}
			/>
		</>
	);
};

export default RoleDetailsDialog;
